import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Trash2, AlertTriangle } from 'lucide-react';

const COLORS = [
  '#6366F1', '#8B5CF6', '#A855F7', '#D946EF', 
  '#EC4899', '#F43F5E', '#EF4444', '#F97316',
  '#F59E0B', '#84CC16', '#22C55E', '#14B8A6',
  '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1'
];

export const MacroProjectDialog = ({ open, onOpenChange, macroProject, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#6366F1'
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(function() {
    if (macroProject) {
      setFormData({
        name: macroProject.name || '',
        description: macroProject.description || '',
        color: macroProject.color || '#6366F1'
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: '#6366F1'
      });
    }
    setShowDeleteConfirm(false);
  }, [macroProject, open]);

  const handleSubmit = function(e) {
    e.preventDefault();
    const data = {
      ...formData
    };
    if (macroProject) {
      data.id = macroProject.id;
    }
    onSave(data);
    onOpenChange(false);
  };

  const handleDelete = function() {
    if (onDelete && macroProject) {
      onDelete(macroProject.id);
      onOpenChange(false);
    }
  };

  const isEditing = macroProject !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Modifica Macro Progetto' : 'Nuovo Macro Progetto'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mp-name">Nome</Label>
            <Input
              id="mp-name"
              value={formData.name}
              onChange={function(e) { setFormData({ ...formData, name: e.target.value }); }}
              placeholder="Es. Portafoglio Q1 2026"
              required
              data-testid="macro-project-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mp-description">Descrizione</Label>
            <Textarea
              id="mp-description"
              value={formData.description}
              onChange={function(e) { setFormData({ ...formData, description: e.target.value }); }}
              placeholder="Descrizione opzionale..."
              rows={3}
              data-testid="macro-project-description-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Colore</Label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map(function(color) {
                return (
                  <button
                    key={color}
                    type="button"
                    className={
                      'w-8 h-8 rounded-full transition-transform ' +
                      (formData.color === color ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-110')
                    }
                    style={{ backgroundColor: color }}
                    onClick={function() { setFormData({ ...formData, color: color }); }}
                  />
                );
              })}
            </div>
          </div>

          {isEditing && !showDeleteConfirm && (
            <Button
              type="button"
              variant="outline"
              className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={function() { setShowDeleteConfirm(true); }}
              data-testid="delete-macro-project-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Elimina Macro Progetto
            </Button>
          )}

          {showDeleteConfirm && (
            <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/5 space-y-3">
              <div className="flex items-start gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">Confermi l'eliminazione?</p>
                  <p className="text-muted-foreground">
                    Verranno eliminati anche tutti i progetti e task associati.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={function() { setShowDeleteConfirm(false); }}
                >
                  Annulla
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                  onClick={handleDelete}
                  data-testid="confirm-delete-macro-project-btn"
                >
                  Elimina
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={function() { onOpenChange(false); }}>
              Annulla
            </Button>
            <Button type="submit" data-testid="save-macro-project-btn">
              {isEditing ? 'Salva' : 'Crea'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MacroProjectDialog;
